import styled from 'styled-components';

/**
 * ページ共通のレイアウト / タイポグラフィ / CTA プリミティブ。
 *
 * 各画面の見た目が乖離するリスクを防止するため単一定義に集約した。
 * 適用済みは設定系 6 画面（`/consent` / `/link_token` / `/menu` / `/menu/profile` / `/menu/families` /`/menu/families/:ids`）。
 * HOME / グラフ / AI チャットは BottomNavigation 用の`padding-bottom` を持つ独自ラッパのままで未移行。
 */

/** ページ全体のラッパ */
export const PageContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.pageBackground};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.md};
  font-family: ${({ theme }) => theme.typography.fontFamily.base};
`;

/**
 * ページ見出し。
 */
export const PageHeading = styled.h1`
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** ページ主要コンテンツを載せる白カード */
export const ContentCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.background};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

/**
 * `ContentCardTitle` / `ContentCardDescription` を束ねる見出しブロック。
 */
export const ContentCardHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

/**
 * `ContentCard` の中に置く見出し
 */
export const ContentCardTitle = styled.p`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/**
 * `ContentCardTitle` に添える説明文
 */
export const ContentCardDescription = styled.p`
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/**
 * 画面下部の主要 CTA ボタン（幅いっぱい）。
 *
 * `-webkit-appearance: none` は iOS Safari / LIFF WebView がネイティブのボタン装飾を
 * 上乗せするのを防ぐ。`background` / `border` を明示しているため見た目は変わらない。
 *
 * `type` の既定を `button` に倒しているのは、`<button>` の HTML 既定が `submit` のため。
 * 将来 `<form>` の中で使われた時にネイティブ submit でページがリロードされると
 * LIFF では画面ごと再初期化されてしまう。呼び出し側で毎回書く運用は漏れるのでここで潰す。
 * `type="submit"` が要る場面では呼び出し側から明示的に上書きできる。
 */
export const PrimaryButton = styled.button.attrs<{ type?: 'button' | 'submit' | 'reset' }>(
  ({ type = 'button' }) => ({ type }),
)`
  width: 100%;
  min-height: ${({ theme }) => theme.accessibility.minTouchTarget};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.ms};
  background: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.inverse};
  text-align: center;
  -webkit-appearance: none;
  appearance: none;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.4;
    box-shadow: none;
    cursor: not-allowed;
  }

  /*
   * iOS Safari / LIFF WebView ではタップ後に :hover が張り付き、別要素をタップするまで解除されない。
   * ホバー可能なポインタを持つ環境（デスクトップブラウザ）に限定して適用する。
   */
  @media (hover: hover) {
    &:hover:not(:disabled) {
      opacity: 0.85;
    }
  }
`;

/** 「戻る」などの副次アクション用のテキストボタン（`type` の既定は `PrimaryButton` と同じ理由で `button`） */
export const TextButton = styled.button.attrs<{ type?: 'button' | 'submit' | 'reset' }>(
  ({ type = 'button' }) => ({ type }),
)`
  display: block;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  background: transparent;
  border: none;
  cursor: pointer;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.primary};

  /* sticky hover を避ける理由は PrimaryButton と同じ */
  @media (hover: hover) {
    &:hover {
      opacity: 0.7;
    }
  }
`;
